import React, { useState, useCallback, useRef } from 'react';
import { Box, Stack, Typography, Chip, Paper } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CreateApi from '../../../shared/Settings/appSettings';
import { StyledAutocompleteInput } from '../../../shared/Components/StyledAutocompleteInput';
import { useTBContext } from '../../Contexts/useTBStates';
import type { UserSearchResult, MomentUser } from '@moment-golf/types';
import { UserProfileSection } from './UserProfileSection';
import { RewardsSection } from './RewardsSection';
import { CreditsDiscountsSection } from './CreditsDiscountsSection';

interface UserManagementProps {
    appDims: any;
    compHeights: any;
}

export const UserManagement = ({ appDims, compHeights }: UserManagementProps) => {
    const { appTheme, factor, viewPanel, selectedUser, setSelectedUser } = useTBContext();
    const api = CreateApi();

    const [searchOptions, setSearchOptions] = useState<UserSearchResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSearchInput = useCallback((inputValue: string) => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        if (!inputValue || inputValue.trim().length < 2) {
            setSearchOptions([]);
            return;
        }
        debounceTimer.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await api.get(`/um/SearchUsers?query=${encodeURIComponent(inputValue.trim())}`);
                setSearchOptions(res.data ?? []);
            } catch {
                setSearchOptions([]);
            } finally {
                setSearchLoading(false);
            }
        }, 350);
    }, [api]);

    const handleUserSelect = useCallback(async (_: any, value: UserSearchResult | null) => {
        if (!value) {
            setSelectedUser(null);
            return;
        }
        try {
            const res = await api.get(`/um/GetUserProfile?userID=${value.userID}`);
            setSelectedUser(res.data as MomentUser);
        } catch {
            setSelectedUser(null);
        }
    }, [api, setSelectedUser]);

    const usableHeight = appDims.appHeight - (compHeights.appBarHeight ?? 0) - (compHeights.appFooterHeight ?? 0);

    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
                height: usableHeight,
                overflow: 'hidden',
            }}
        >
            {/* ── User Search Header ───────────────────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'sepia.extraLight',
                    backgroundColor: 'sepia.background',
                    px: 2,
                    py: 1.5,
                    flexShrink: 0,
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography
                        variant="subtitle2"
                        color="primary.main"
                        sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12 * (factor < 1 ? factor : 1) }}
                    >
                        USER SEARCH
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 260, maxWidth: 420 }}>
                        <StyledAutocompleteInput
                            label="Search by name or email"
                            factor={factor}
                            appTheme={appTheme}
                            options={searchOptions}
                            loading={searchLoading}
                            getOptionLabel={(opt: UserSearchResult) => `${opt.displayName} (${opt.userEmail})`}
                            isOptionEqualToValue={(opt: UserSearchResult, val: UserSearchResult) => opt.userID === val.userID}
                            filterOptions={(x: UserSearchResult[]) => x}
                            onInputChange={(_: any, val: string) => handleSearchInput(val)}
                            onChange={handleUserSelect}
                            noOptionsText="Type at least 2 characters to search"
                            renderOption={(props: any, opt: UserSearchResult) => (
                                <li {...props} key={opt.userID}>
                                    {opt.displayName} ({opt.userEmail})
                                </li>
                            )}
                        />
                    </Box>
                    {selectedUser && (
                        <Chip
                            icon={<PersonIcon />}
                            label={`${selectedUser.displayName}  ·  ${selectedUser.userEmail}`}
                            color="primary"
                            variant="outlined"
                            onDelete={() => setSelectedUser(null)}
                            sx={{ fontSize: 11 * (factor < 1 ? factor : 1) }}
                        />
                    )}
                </Stack>
            </Paper>

            {/* ── Section Content ──────────────────────────────────────── */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {!selectedUser ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                        <PersonIcon sx={{ fontSize: 56, color: 'sepia.extraLight' }} />
                        <Typography variant="body2" color="text.secondary" mt={1}>
                            Search for a user by name or email to get started.
                        </Typography>
                    </Stack>
                ) : (
                    <>
                        {viewPanel === 'userProfile' && (
                            <UserProfileSection appDims={appDims} compHeights={compHeights} />
                        )}
                        {viewPanel === 'rewards' && (
                            <RewardsSection appDims={appDims} compHeights={compHeights} />
                        )}
                        {viewPanel === 'creditsDiscounts' && (
                            <CreditsDiscountsSection appDims={appDims} compHeights={compHeights} />
                        )}
                    </>
                )}
            </Box>
        </Stack>
    );
};
